import React, { useState, useEffect, useRef } from 'react';
import { User, Bot, Copy, CheckCheck, ThumbsUp, ThumbsDown, RefreshCw, CornerDownLeft } from 'lucide-react';
import { Message } from '../../types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github.css';

interface ChatMessageProps {
  message: Message;
  onFeedback?: (messageId: string, feedback: 'positive' | 'negative') => void;
  onRegenerate?: (messageId: string) => void;
}

export function ChatMessage({
  message,
  onFeedback,
  onRegenerate
}: ChatMessageProps) {
  const [isCopied, setIsCopied] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const messageRef = useRef<HTMLDivElement>(null);

  const isUser = message.role === 'user';

  // Typing animation for AI messages
  const [displayedContent, setDisplayedContent] = useState('');
  const [isTyping, setIsTyping] = useState(message.role === 'assistant');

  useEffect(() => {
    if (isTyping && message.role === 'assistant') {
      let currentIndex = 0;
      const typingEffect = setInterval(() => {
        if (currentIndex < message.content.length) {
          setDisplayedContent(prev => prev + message.content[currentIndex]);
          currentIndex++;
        } else {
          clearInterval(typingEffect);
          setIsTyping(false);
        }
      }, 20); // Adjust typing speed as needed

      return () => clearInterval(typingEffect);
    }
  }, [message.content, message.role]);

  const handleCopyMessage = () => {
    navigator.clipboard.writeText(message.content);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const handlePositiveFeedback = () => {
    onFeedback?.(message.id, 'positive');
    setShowFeedback(false);
  };

  const handleNegativeFeedback = () => {
    onFeedback?.(message.id, 'negative');
    setShowFeedback(false);
  };

  const handleRegenerate = () => {
    onRegenerate?.(message.id);
  };

  // Scroll to bottom when message is added
  useEffect(() => {
    messageRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [message.content]);

  return (
    <div
      ref={messageRef}
      className={`
        flex transition-all duration-300 ease-in-out 
        ${isUser ? 'justify-end' : 'justify-start'} 
        mb-4 animate-fade-in
      `}
    >
      <div
        className={`
          flex items-start space-x-2 max-w-[90%] group 
          ${isUser ? 'flex-row-reverse space-x-reverse' : 'flex-row'}
        `}
      >
        {/* Avatar */}
        <div
          className={`
            w-10 h-10 rounded-full flex items-center justify-center 
            flex-shrink-0 shadow-sm transition-all 
            ${isUser
              ? 'bg-blue-100 dark:bg-blue-900'
              : 'bg-gray-100 dark:bg-gray-700'}
            group-hover:scale-105
          `}
        >
          {isUser ? (
            <User className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          ) : (
            <Bot className="w-6 h-6 text-gray-600 dark:text-gray-400" />
          )}
        </div>

        {/* Message Container */}
        <div className="relative max-w-full">
          <div
            className={`
              p-3 rounded-lg break-words 
              ${isUser
                ? 'bg-blue-600 dark:bg-blue-500 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'}
              ${isUser ? 'rounded-tr-sm' : 'rounded-tl-sm'}
              max-w-[350px] md:max-w-[500px] 
              overflow-x-auto whitespace-pre-wrap
              relative
            `}
          >
            {/* Markdown rendering for better formatting */}
            {isTyping ? (
              <span className="typing-cursor">
                {displayedContent}
                <span className="animate-blink">|</span>
              </span>
            ) : (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeHighlight]}
                components={{
                  code: ({ node, inline, className, children, ...props }) => {
                    return !inline ? (
                      <div className="relative">
                        <pre
                          className={`
                            ${className} 
                            bg-gray-50 dark:bg-gray-800 
                            p-2 rounded-md overflow-x-auto 
                            text-sm mt-2 mb-2
                          `}
                        >
                          <code {...props}>{children}</code>
                        </pre>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(String(children));
                            // Optional: add copy feedback
                          }}
                          className="
                            absolute top-2 right-2 
                            bg-gray-200 dark:bg-gray-700 
                            p-1 rounded 
                            hover:bg-gray-300 dark:hover:bg-gray-600
                          "
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <code
                        className={`
                          ${className} 
                          bg-gray-100 dark:bg-gray-800 
                          px-1 rounded 
                          text-sm
                        `}
                        {...props}
                      >
                        {children}
                      </code>
                    );
                  }
                }}
              >
                {message.content}
              </ReactMarkdown>
            )}
          </div>

          {/* Action Buttons for AI Messages */}
          {!isUser && (
            <div className="flex items-center space-x-2 mt-1">
              {/* Copy Button */}
              <button
                onClick={handleCopyMessage}
                className="
                  text-gray-500 hover:text-gray-700 
                  dark:text-gray-400 dark:hover:text-gray-200
                  transition-colors duration-200
                "
                aria-label="Copy message"
              >
                {isCopied ? (
                  <CheckCheck className="w-4 h-4 text-green-500" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </button>

              {/* Feedback Buttons */}
              <div className="relative">
                <button
                  onClick={() => setShowFeedback(!showFeedback)}
                  className="
                    text-gray-500 hover:text-gray-700 
                    dark:text-gray-400 dark:hover:text-gray-200
                    transition-colors duration-200
                  "
                  aria-label="Provide feedback"
                >
                  <CornerDownLeft className="w-4 h-4" />
                </button>

                {showFeedback && (
                  <div
                    className="
                      absolute left-0 top-full z-10 
                      bg-white dark:bg-gray-800 
                      shadow-lg rounded-md 
                      flex items-center space-x-2 
                      p-1 mt-1
                    "
                  >
                    <button
                      onClick={handlePositiveFeedback}
                      className="hover:bg-green-100 dark:hover:bg-green-900 p-1 rounded"
                      aria-label="Thumbs up"
                    >
                      <ThumbsUp className="w-4 h-4 text-green-500" />
                    </button>
                    <button
                      onClick={handleNegativeFeedback}
                      className="hover:bg-red-100 dark:hover:bg-red-900 p-1 rounded"
                      aria-label="Thumbs down"
                    >
                      <ThumbsDown className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                )}
              </div>

              {/* Regenerate Button */}
              {onRegenerate && (
                <button
                  onClick={handleRegenerate}
                  className="
                    text-gray-500 hover:text-gray-700 
                    dark:text-gray-400 dark:hover:text-gray-200
                    transition-colors duration-200
                  "
                  aria-label="Regenerate response"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Add some custom CSS for typing effect and animations
const styles = `
  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
  }
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  .typing-cursor .animate-blink {
    animation: blink 0.7s infinite;
  }
  
  .animate-fade-in {
    animation: fadeIn 0.3s ease-out;
  }
`;

// Inject styles
const styleSheet = document.createElement("style")
styleSheet.type = "text/css"
styleSheet.innerText = styles
document.head.appendChild(styleSheet);